import React from 'react';
import { FormInput, FormSelect, Button } from '../index';
import styles from './FilterBar.module.css';

/**
 * FilterBar Component
 * Renders a standardized set of filter inputs and a clear button.
 *
 * @param {Object} props
 * @param {Array} props.filters - Array of filter configurations
 * @param {Function} props.onClear - Handler for clearing all filters
 * @param {React.ReactNode} props.children - Optional additional actions
 * @param {boolean} props.showSearch - Show search field (default: true)
 * @param {boolean} props.showFilters - Show filter fields (default: true)
 * @param {string} props.searchPlaceholder - Custom placeholder for search
 */
const FilterBar = ({
    filters = [],
    onClear,
    children,
    showSearch = true,
    showFilters = true,
    searchPlaceholder = 'Search...'
}) => {
    // Separate search from other filters
    const searchFilter = filters.find(f => f.type === 'search' || f.name === 'search');
    const otherFilters = filters.filter(f => f.type !== 'search' && f.name !== 'search');

    // Don't render if both are disabled
    if (!showSearch && !showFilters) return null;

    return (
        <div className={styles.filterBar}>
            {showSearch && searchFilter && (
                <div className={styles.searchSection}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder={searchFilter.placeholder || searchPlaceholder}
                        value={searchFilter.value}
                        onChange={searchFilter.onChange}
                    />
                </div>
            )}

            {showFilters && otherFilters.length > 0 && (
                <div className={styles.filtersSection}>
                    {otherFilters.map((filter) => {
                        const { type, name, label, value, onChange, options, placeholder, ...rest } = filter;

                        return (
                            <div key={name} className={styles.filterGroup}>
                                {type === 'select' && (
                                    <FormSelect
                                        label={label}
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        options={options}
                                        {...rest}
                                    />
                                )}

                                {(type === 'text' || type === 'input') && (
                                    <FormInput
                                        label={label}
                                        name={name}
                                        type="text"
                                        placeholder={placeholder}
                                        value={value}
                                        onChange={onChange}
                                        {...rest}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {(onClear || children) && (
                <div className={styles.filterActions}>
                    {onClear && (
                        <Button
                            onClick={onClear}
                            variant="secondary"
                        >
                            CLEAR FILTERS
                        </Button>
                    )}
                    {children}
                </div>
            )}
        </div>
    );
};

export default FilterBar;
